import { FC, useState } from 'react';
import ImageIconSolid from '@/components/icons/ImageIconSolid';
import EImageWithFallback from '@/components/everridge/EImageWithFallback';
import {
	GetNewAndOfferListItemResponseDto,
	NewsAndOffersStatus,
} from '@/features/news-and-offers/types/news-and-offers-dto';
import { DateTimeHelper } from '@/utils/dateTimeHelper';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import Archive_02_Icon from '@/components/icons/Archive_02_Icon';
import ComposeIcon from '@/components/icons/ComposeIcon';
import { newsAndOffersConfig } from '@/features/news-and-offers/utils/newsAndOffersConfig';
import MoreVerticalIcon from '@/components/icons/MoreVerticalIcon';
import EDropDownMenu from '@/components/everridge/EDropDownMenu';
import Delete_02_Icon from '@/components/icons/Delete_02_Icon';
import NewAndOfferArchiveModal from '@/features/news-and-offers/components/NewAndOfferArchiveModal';
import { useNewAndOfferDetailsApi } from '@/features/news-and-offers/hooks/useNewAndOfferDetailsApi';
import { ImagesHelper } from '@/utils/ImagesHelper';
import { Roles } from '@/@types/auth.types.ts';
import { useUserRole } from '@/features/identity/hooks/useUserRole.ts';
import SendIcon from '@/components/icons/SendIcon';

type Props = {
	showDetails: (id: string) => void;
	onEdit?: (id: string) => void;
	isMobile?: boolean;
} & GetNewAndOfferListItemResponseDto;

const NewsAndOffersCardMobile: FC<Props> = ({
	id,
	startDate,
	endDate,
	title,
	description,
	type,
	status,
	imageUrl,
	showDetails,
	onEdit,
	isMobile,
}) => {
	const [showArchiveWarning, setShowArchiveWarning] = useState(false);
	const { t } = useTranslation();
	const userRole = useUserRole();

	const { deleteNewAndOffer, changeNewAndOfferStatus } =
		useNewAndOfferDetailsApi();

	const {
		label,
		icon: Icon,
		className,
	} = newsAndOffersConfig.getCardIcon(type);

	const handleShowArchiveWarning = () => {
		setShowArchiveWarning(true);
	};

	const handleArchiveSelected = async () => {
		await changeNewAndOfferStatus({
			id,
			status: NewsAndOffersStatus.Archived,
		});
		setShowArchiveWarning(false);
	};

	const handleDeleteSelected = async () => {
		await deleteNewAndOffer(id);
	};

	const handlePostSelected = async () => {
		await changeNewAndOfferStatus({
			id,
			status: NewsAndOffersStatus.Active,
		});
		setShowArchiveWarning(false);
	};

	const draftButtons = (
		<>
			<EDropDownMenu
				icon={MoreVerticalIcon}
				buttonClassName='relative flex-1'>
				<Button
					variant='ghost'
					className='justify-start rounded-none!'
					onClick={() => onEdit?.(id)}>
					<ComposeIcon />
					<span className='truncate text-inherit'>
						{t('elements.buttons.edit')}
					</span>
				</Button>
				<Button
					variant='ghost'
					className='justify-start rounded-none! text-error-1000 active:bg-error-100'
					onClick={handleDeleteSelected}>
					<Delete_02_Icon className='stroke-error-1000' />
					<span className='truncate text-inherit'>
						{t('elements.buttons.delete')}
					</span>
				</Button>
			</EDropDownMenu>
			<Button
				variant='default'
				className='relative flex-1 max-w-1/2'
				onClick={handlePostSelected}>
				<SendIcon className='stroke-popover-10' />
			</Button>
		</>
	);

	const activeButtons = (
		<>
			<Button
				variant='outline'
				className='relative! flex-1 text-error-1000 active:bg-error-100 max-w-1/2'
				onClick={handleShowArchiveWarning}>
				<Archive_02_Icon className='stroke-error-1000' />
			</Button>
			<Button
				variant='outline'
				className='relative flex-1 max-w-1/2'
				onClick={() => onEdit?.(id)}>
				<ComposeIcon />
			</Button>
		</>
	);

	const archivedButtons = (
		<>
			<div className='relative w-full rounded-full'>
				<Button
					variant='outline'
					className='w-full text-error-1000 active:bg-error-100'
					disabled={true}>
					<Archive_02_Icon className='stroke-popover-400' />
					<span className='truncate text-inherit'>
						{t('elements.buttons.archived')}
					</span>
				</Button>
			</div>
		</>
	);

	return (
		<div className='relative -mx-4 p-4 flex gap-4 bg-popover-10 hover:bg-popover-50 active:bg-popover-100 cursor-pointer'>
			<div
				className='absolute inset-0'
				onClick={() => showDetails(id)}
			/>
			<EImageWithFallback
				src={imageUrl}
				fallbackSVG={ImageIconSolid}
				containerClassName='max-w-1/2 flex-1 rounded-semi-xl overflow-hidden'
			/>
			<div className='max-w-1/2 flex-1 flex flex-col gap-2'>
				<div className='flex items-center gap-1.5'>
					<Icon className={className} />
					<h5 className='text-sm text-popover-800'>{t(label)}</h5>
				</div>
				<div className='flex flex-col'>
					<h4 className='text-base text-popover-1000 font-medium line-clamp-1'>
						{title}
					</h4>
					<p className='h-15 text-sm text-popover-800 line-clamp-3'>
						{description ??
							t('main.apartments.components.card.noDescription')}
					</p>
				</div>
				<span className='text-sm text-popover-800'>
					{DateTimeHelper.formatDate(startDate, 2)}
				</span>
				{userRole === Roles.admin && (
					<div className='mt-auto flex gap-2 select-none'>
						{status === NewsAndOffersStatus.Draft && draftButtons}
						{status === NewsAndOffersStatus.Active && activeButtons}
						{status === NewsAndOffersStatus.Archived &&
							archivedButtons}
					</div>
				)}
			</div>
			{showArchiveWarning && (
				<NewAndOfferArchiveModal
					onSubmit={handleArchiveSelected}
					onClose={() => setShowArchiveWarning(false)}
				/>
			)}
		</div>
	);
};

export default NewsAndOffersCardMobile;
